import { useState, lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import HubSpotTracker from "@/components/HubSpotTracker";

// Scrolls to in-page anchor (#id) when the URL hash changes or a new route loads with one.
const HashScrollHandler = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    let attempts = 0;
    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 80) setTimeout(tryScroll, 100);
    };
    setTimeout(tryScroll, 100);
    return () => { cancelled = true; };
  }, [pathname, hash]);
  return null;
};

// Critical path
import Index from "./pages/Index";

// Lazy-loaded routes
const ScheduleCall = lazy(() => import("./pages/ScheduleCall"));
const Solutions = lazy(() => import("./pages/Solutions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SmsTerms = lazy(() => import("./pages/SmsTerms"));
const M365Explorer = lazy(() => import("./pages/M365Explorer"));
const M365SecurityAssessment = lazy(() => import("./pages/M365SecurityAssessment"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const CustomerCare = lazy(() => import("./pages/CustomerCare"));
const AddOns = lazy(() => import("./pages/AddOns"));
const CustomerSuccessCRM = lazy(() => import("./pages/CustomerSuccessCRM"));
const CustomerSuccessBusinessCentral = lazy(() => import("./pages/CustomerSuccessBusinessCentral"));
const Implementations = lazy(() => import("./pages/Implementations"));
const D365SuperEmail = lazy(() => import("./pages/D365SuperEmail"));
const D365SuperEmailSupport = lazy(() => import("./pages/D365SuperEmailSupport"));
const HelpDesk = lazy(() => import("./pages/HelpDesk"));
const NotFound = lazy(() => import("./pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <HashScrollHandler />
          <HubSpotTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/schedule-a-call" element={<ScheduleCall />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/sms-terms" element={<SmsTerms />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* D365 offering pages */}
              <Route path="/customer-care" element={<CustomerCare />} />
              <Route path="/add-ons" element={<AddOns />} />
              <Route path="/customer-success-crm" element={<CustomerSuccessCRM />} />
              <Route path="/customer-success-business-central" element={<CustomerSuccessBusinessCentral />} />
              <Route path="/implementations" element={<Implementations />} />
              <Route path="/superemail" element={<Navigate to="/d365superemail" replace />} />
              <Route path="/d365superemail" element={<D365SuperEmail />} />
              <Route path="/d365superemail/support" element={<D365SuperEmailSupport />} />
              <Route path="/help-desk" element={<HelpDesk />} />
              {/* Legacy M365 routes → home */}
              <Route path="/m365-success" element={<Navigate to="/" replace />} />
              <Route path="/client-success" element={<Navigate to="/" replace />} />
              <Route path="/migration" element={<Navigate to="/" replace />} />
              <Route path="/cloud" element={<Navigate to="/" replace />} />
              <Route path="/secure" element={<Navigate to="/" replace />} />
              <Route path="/managed" element={<Navigate to="/" replace />} />
              <Route path="/msp" element={<Navigate to="/" replace />} />
              <Route path="/m365-explorer" element={<Navigate to="/" replace />} />
              <Route path="/m365-security-assessment" element={<Navigate to="/" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
